/**
 * Single source of truth for public-facing facts. Update values here rather than
 * hard-coding them across pages. See docs/CONTENT_GUIDE.md for how to edit content
 * and docs/LEGAL_REVIEW.md for values that are still pending business/legal input.
 */

export const siteConfig = {
  name: "Devory",
  legalDeveloper: "netraTEC",
  /**
   * The legal seller behind Devory/netraTEC (owner decision, 2026-08-20,
   * SUPERSEDING the 2026-08-18 decision below). Devory is initially trading as
   * a sole-trader operation, not through SAMADROCK LTD -- SAMADROCK LTD is a
   * separate incorporated entity and is NOT the Devory subscription seller
   * during this phase. A dedicated Devory company may be incorporated later
   * (planned ~6 months out, not a fixed date), at which point this record and
   * every page that reads it must be updated together as a deliberate cutover,
   * not a silent edit -- historical sole-trader transactions stay attributable
   * to the sole trader, never retroactively re-owned by the future company.
   * See docs/LEGAL_REVIEW.md for the full reconciliation note.
   *
   * `structure: "Sole trader"` has no company number (sole traders don't have
   * one) and no "registered office" (a company-law concept) -- `serviceAddress`
   * is the UK trading-disclosure address instead: required because "Devory" is
   * not the trader's own surname (Company, LLP and Business (Names and Trading
   * Disclosures) Regulations 2015), shown on the website/Terms/checkout/
   * invoices, not reused as a support/contact address for routine enquiries.
   */
  legalEntity: {
    name: "Mohamed Anise",
    structure: "Sole trader",
    tradingAs: "Devory",
    jurisdiction: "England and Wales",
    serviceAddress: "1106 Northill Apartments, Furness Quay, Salford Quays, M50 3DL",
    /**
     * Owner-confirmed fact, 2026-08-18, still current under the sole-trader
     * model: not a permanent property. Re-verify against real HMRC
     * registration status before treating this as still current -- see the
     * VAT-monitoring requirement in docs/LEGAL_REVIEW.md. Never hard-code the
     * assumption that this stays false forever; it gates whether VAT wording,
     * Stripe Tax, and invoice VAT lines are ever activated. This repository
     * does not determine or advise on the owner's personal VAT/HMRC
     * obligations -- it only reflects the current confirmed status in
     * customer-facing text.
     */
    vatRegistered: false,
  },
  /** The one description used consistently across the site — see the
   * 2026-07-18 brand redesign pass. Never "engineering developer
   * ecosystem"; that phrasing is retired. */
  tagline:
    "Devory is a graph-native software engineering platform: components are composed, connected, tested, executed and published as one governed system.",
  /**
   * Canonical production origin used for canonical URLs, sitemap.xml, robots.txt and
   * Open Graph metadata. Confirmed live 2026-07-27 via direct HTTP requests to both
   * attached custom domains. `devory.app` (apex) now issues a 308 redirect to this host
   * from worker/index.ts, matching the two-hostname apex-redirects-to-www model in
   * DEVORY_APP_DOMAIN_AND_DNS_RUNBOOK.md. This must always match a domain that is
   * *actually* serving this deployment — see docs/DEPLOYMENT.md.
   */
  url: "https://www.devory.app",
  contactEmail: "dev@devory.app",
  /**
   * Updated 2026-09-13: Public Release 0.1 has been live since 2026-08-07 -- real self-serve
   * sign-up, real Stripe billing, real (scoped) cloud execution -- see signInUrl's own note.
   * "In active development" still describes the platform honestly (most of the product surface,
   * especially Runtime/AUI, is still being built), but it must never again imply sign-up itself
   * is closed; those are two separate claims.
   */
  stage: {
    label: "Live",
    /** One-line condensation of `summary`, for places that already show `label` via a StatusBadge and would otherwise repeat it. Must stay consistent with `summary`. */
    note: "Public Release 0.1 is live — create a free account and start building.",
    summary:
      "Devory Public Release 0.1 is live. Core building, real organisations and billing, and a scoped set of cloud-execution and marketplace capabilities are open for public self-serve sign-up today; the wider platform is still in active development.",
  },
} as const;

export type NavItem = {
  label: string;
  path: string;
};

/**
 * Seven items, ordered so the story holds even in a menu: Product and Marketplace
 * establish composition and trust before For AI introduces agent identity; Solutions
 * and Enterprise are audience-specific entry points; Docs and Pricing close the list.
 * `/docs` is now real (see src/pages/DocsPage.tsx) and belongs here — a nav link to
 * nothing was a broken promise; a nav link to an honestly thin page is not. Company
 * and Contact remain reachable from the persistent header CTA and the footer's
 * Company group, not the primary nav, so this list stays focused on evaluation, not
 * navigation to every page. See DEVORY_WEBSITE_BLUEPRINT.md Part III.
 */
export const primaryNav: NavItem[] = [
  { label: "Product", path: "/product" },
  { label: "Marketplace", path: "/marketplace" },
  { label: "For AI", path: "/agent-user-interface" },
  { label: "Solutions", path: "/solutions" },
  { label: "Enterprise", path: "/enterprise" },
  { label: "Docs", path: "/docs" },
  { label: "Pricing", path: "/pricing" },
];

export const primaryCta: NavItem = { label: "Contact Devory", path: "/contact" };
export const secondaryCta: NavItem = { label: "Explore the platform", path: "/product" };

/**
 * Domain decision (D2, V1 release convergence): `app.devory.app` is the
 * canonical Devory customer-application domain — not `platform.devory.app`,
 * which is the API Gateway's own domain, never a customer-facing surface.
 *
 * Repointed 2026-09-13: Cloud sign-in is genuinely public now (Public Release
 * 0.1, live since 2026-08-07) — real self-serve sign-up, real Stripe billing,
 * real (scoped) cloud execution. Verified directly: app.devory.app/signin
 * serves a real sign-in form with a working "Create one" self-serve sign-up
 * link. This no longer gateways through the signin.devory.app holding page.
 */
export const signInUrl = "https://app.devory.app/signin";
export const signInCta: NavItem = { label: "Sign in", path: signInUrl };

/** Same real, public app.devory.app SPA as signInUrl, its self-serve account-creation route. */
export const signUpUrl = "https://app.devory.app/signup";
export const signUpCta: NavItem = { label: "Create your account", path: signUpUrl };

export type FooterGroup = {
  heading: string;
  links: NavItem[];
};

export const footerGroups: FooterGroup[] = [
  {
    heading: "Product",
    links: [
      { label: "Product overview", path: "/product" },
      { label: "Canvas", path: "/canvas" },
      { label: "Components", path: "/components" },
      { label: "Testing and qualification", path: "/qualification" },
      { label: "For AI (AUI & MCP)", path: "/agent-user-interface" },
      { label: "Devory Cloud", path: "/cloud" },
      signInCta,
    ],
  },
  {
    heading: "Platform",
    links: [
      { label: "Marketplace", path: "/marketplace" },
      { label: "Docs", path: "/docs" },
      { label: "Pricing", path: "/pricing" },
      { label: "Solutions", path: "/solutions" },
      { label: "Enterprise", path: "/enterprise" },
      { label: "Security", path: "/security" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", path: "/about" },
      { label: "Contact", path: "/contact" },
      { label: "Support", path: "/support" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Legal hub", path: "/legal" },
      { label: "Terms of Service", path: "/legal/terms" },
      { label: "Privacy Policy", path: "/legal/privacy" },
      { label: "Refund Policy", path: "/legal/refunds" },
      { label: "Cancellation Policy", path: "/legal/cancellation" },
      { label: "Acceptable Use Policy", path: "/legal/acceptable-use" },
      { label: "Cookie Policy", path: "/legal/cookies" },
    ],
  },
];

/** Every statically prerendered route. Used by the prerender script and sitemap generator. */
export const staticRoutes = [
  "/",
  "/product",
  "/canvas",
  "/components",
  "/qualification",
  "/marketplace",
  "/agent-user-interface",
  "/docs",
  "/cloud",
  "/solutions",
  "/pricing",
  "/enterprise",
  "/security",
  "/about",
  "/contact",
  "/support",
  "/legal",
  "/legal/terms",
  "/legal/privacy",
  "/legal/refunds",
  "/legal/cancellation",
  "/legal/acceptable-use",
  "/legal/cookies",
] as const;
